import { Fragment, lazy, ReactNode, Suspense, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { CookieConsent, IdleTimeoutHandler, Page403, Page404, Page500 } from '@zeta-commons';
import { axiosInstance, enqueueSnackbar, LoadingScreen } from '@zeta-core';
import { ErrorStatusCodes, getErrorMessage, ServerErrorMessage } from '@zeta-services';
import { AuthGuard } from '../../auth/auth-guard';
import CompactLayout, { CompactErrorPage } from '../../layouts/compact-layout';
import AuthRoutes from './auth-routes';
import DashboardRoutes from './dashboard-routes';
import LoggedOutErrorBoundary from './logged-out-error-boundary';
import ProfileSetupRoutes from './profile-setup-routes';
import PublicRoutes from './public-routes';
import RegistrationRoutes from './register-routes';
import RiskRoutes from './risk-questionnaire';
import SignUpRoutes from './signup-routes';
import RestrictedAccess from '../../components/restricted-access';

const HomePage = lazy(() => import('../../pages/home'));
const Pricing = lazy(() => import('../../pages/pricing'));
const KycStatusPage = lazy(() => import('../../pages/kyc-status'));

// ----------------------------------------------------------------------

interface IRoute {
  path?: string;
  index?: boolean;
  element?: ReactNode;
  children?: IRoute[];
}

export default function Router() {
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.response.use(
      (response) => {
        if (ErrorStatusCodes?.includes(response.status)) {
          enqueueSnackbar(getErrorMessage({ response }, undefined, ServerErrorMessage), {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          });
        }

        return response;
      },
      (error) => {
        if (ErrorStatusCodes?.includes(error?.response?.status)) {
          enqueueSnackbar(getErrorMessage(error, undefined, ServerErrorMessage), {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          });
        }

        return Promise.reject(error);
      }
    );

    return () => axiosInstance.interceptors.response.eject(interceptorId);
  }, []);

  const routes = useMemo(
    (): RouteObject[] => [
      {
        path: '/',
        element: <LoggedOutErrorBoundary />,
        children: [
          {
            path: '',
            element: (
              <ErrorBoundary FallbackComponent={CompactErrorPage}>
                <Suspense fallback={<LoadingScreen />}>
                  <AuthGuard>
                    <HomePage />
                  </AuthGuard>
                </Suspense>
              </ErrorBoundary>
            ),
          },
        ],
      },
      ...AuthRoutes,
      ...SignUpRoutes,
      ...DashboardRoutes,
      ...RiskRoutes,
      ...PublicRoutes,
      ...ProfileSetupRoutes,
      ...RegistrationRoutes,
      {
        path: 'pricing',
        element: (
          <ErrorBoundary FallbackComponent={CompactErrorPage}>
            <Suspense fallback={<LoadingScreen />}>
              <AuthGuard>
                <Pricing />
              </AuthGuard>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'upgrade-plan',
        element: (
          <ErrorBoundary FallbackComponent={CompactErrorPage}>
            <Suspense fallback={<LoadingScreen />}>
              <AuthGuard>
                <RestrictedAccess />
              </AuthGuard>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'kyc-status',
        element: (
          <ErrorBoundary FallbackComponent={CompactErrorPage}>
            <Suspense fallback={<LoadingScreen />}>
              <KycStatusPage />
            </Suspense>
          </ErrorBoundary>
        ),
      },

      {
        path: '500',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page500 />
          </CompactLayout>
        ),
      },
      {
        path: '403',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page403 />
          </CompactLayout>
        ),
      },
      {
        path: '404',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page404 />
          </CompactLayout>
        ),
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
    []
  );

  const convertRoutes = useCallback((rs?: IRoute[], parentKey = 'root') => {
    return rs?.map((route, index) => {
      const routeKey = route.index ? `${parentKey}-index-${index}` : `${parentKey}-${route.path ?? 'pathless'}-${index}`;

      return (
        <Fragment key={routeKey}>
          <Route {...route}>{convertRoutes(route?.children, routeKey)}</Route>
        </Fragment>
      );
    });
  }, []);
  const componentRoutes = useMemo(() => convertRoutes(routes), [convertRoutes, routes]);

  const router = createBrowserRouter(createRoutesFromElements(componentRoutes));
  return (
    <>
      <IdleTimeoutHandler />
      <RouterProvider router={router} />
      <CookieConsent />
    </>
  );
}
